import React, { useEffect, useState } from 'react';
import {
  Badge,
  BadgeProps,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { track } from '../../api/track';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { Logo } from './Logo';
import LogoutIcon from '@mui/icons-material/Logout';

export const LobbyBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontSize: 16,
    border: `2px solid`,
    borderColor: '#9affd0',
    borderRadius: 4,
    padding: '5px 8px',
    color: '#9affd0',
    textShadow:
      '#9affd0 0px 0px 0px, #9affd0 0px 0px 0px, #9affd0 0px 0px 16px',
  },
}));

type ActiveTabs = 'home' | 'profile' | 'lobby' | 'explore' | 'admin' | 'league';

type PropTypes = {
  handleDrawerClose: () => void;
  handleLogoutClick: () => void;
  onUpdateProfile: () => void;
  openDrawer: boolean;
  openGames?: Game[];
  profile: Profile;
};

export const MobileNavDrawer = ({
  handleDrawerClose,
  handleLogoutClick,
  onUpdateProfile,
  openDrawer,
  openGames,
  profile,
}: PropTypes) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const getActiveTab = () => {
    if (location.pathname === '/') return 'home';
    if (location.pathname.includes('profile')) return 'profile';
    if (location.pathname.includes('lobby')) return 'lobby';
    if (location.pathname.includes('explore')) return 'explore';
    if (location.pathname.includes('league')) return 'league';
    if (location.pathname.includes('admin')) return 'admin';
  };

  const [activeTab, setActiveTab] = useState<ActiveTabs>(getActiveTab);

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location]);

  const handleProfileClick = async () => {
    if (!profile?.id) return;
    await track(profile?.id, 'header.profile.link');
    navigate(`/profile/overview/${profile?.id}`);
  };

  const handleMainClick = () => navigate('/');
  const handleLeaderboards = () => navigate('/explore/leaderboards');
  const handleLobby = () => navigate('/lobby');
  const handleAdminClick = () => navigate('/admin/dashboard');
  const handleTourney = () => navigate('/tournament');
  const handleLeagueClick = () => navigate('/league/lounge');

  return (
    <Drawer anchor={'left'} open={openDrawer} onClose={handleDrawerClose}>
      <Box
        sx={{
          width: 'auto',
        }}
        role="presentation"
      >
        <Box display={'flex'} justifyContent={'center'} mt={2}>
          <Logo handleLogoClick={() => {}} small />
        </Box>
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleMainClick}>
              <ListItemText
                primary={
                  <Typography
                    variant={activeTab === 'home' ? 'body1led' : 'body1'}
                    letterSpacing={5}
                    sx={{
                      color: activeTab !== 'home' ? 'secondary.light' : null,
                      filter:
                        activeTab === 'home'
                          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
                          : null,
                    }}
                  >
                    Home
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleProfileClick}>
              <ListItemText
                primary={
                  <Typography
                    variant={activeTab === 'profile' ? 'body1led' : 'body1'}
                    letterSpacing={5}
                    sx={{
                      color: activeTab !== 'profile' ? 'secondary.light' : null,
                      filter:
                        activeTab === 'profile'
                          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
                          : null,
                    }}
                  >
                    Profile
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLobby}>
              <ListItemText
                primary={
                  <LobbyBadge badgeContent={openGames?.length}>
                    <Typography
                      variant={activeTab === 'lobby' ? 'body1led' : 'body1'}
                      letterSpacing={5}
                      sx={{
                        color: activeTab !== 'lobby' ? 'secondary.light' : null,
                        filter:
                          activeTab === 'lobby'
                            ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
                            : null,
                      }}
                    >
                      Lobby
                    </Typography>
                  </LobbyBadge>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLeaderboards}>
              <ListItemText
                primary={
                  <Typography
                    variant={activeTab === 'explore' ? 'body1led' : 'body1'}
                    letterSpacing={5}
                    sx={{
                      color: activeTab !== 'explore' ? 'secondary.light' : null,
                      filter:
                        activeTab === 'explore'
                          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
                          : null,
                    }}
                  >
                    Explore
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLeagueClick}>
              <ListItemText
                primary={
                  <Typography
                    variant={activeTab === 'league' ? 'body1led' : 'body1'}
                    letterSpacing={5}
                    sx={{
                      color:
                        activeTab !== 'league'
                          ? theme.palette.ranked.main
                          : '#48d2ff',
                      filter:
                        activeTab === 'league'
                          ? 'drop-shadow(0 0 15px #0266bd) drop-shadow(0 0 50px #0266bd) contrast(2)'
                          : null,
                    }}
                  >
                    League
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          {profile.admin ? (
            <ListItem disablePadding>
              <ListItemButton onClick={handleAdminClick}>
                <ListItemText
                  primary={
                    <Typography
                      variant={activeTab === 'admin' ? 'body1led' : 'body1'}
                      letterSpacing={5}
                      sx={{
                        color: activeTab !== 'admin' ? 'secondary.light' : null,
                        filter:
                          activeTab === 'admin'
                            ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
                            : null,
                      }}
                    >
                      Admin
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ) : null}
          <ListItem disablePadding sx={{ mt: 10 }}>
            <ListItemButton onClick={handleLogoutClick}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon sx={{ color: 'secondary.light' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography letterSpacing={5} color={'secondary.light'}>
                    Logout
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};
